import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Button from '@atlaskit/button';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import IconRemove from '@atlaskit/icon/glyph/editor/remove';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import InlineDialog from '@atlaskit/inline-dialog';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { N100 } from '@atlaskit/theme/colors';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { Repository, Schedule } from 'src/components/pipelines/models';
import { LoadingStatus } from 'src/constants/loading-status';
import { useStatsigGate } from 'src/contexts/feature-provider';

import {
  DOCS_SCHEDULED_PIPELINES,
  INCREASED_MAX_SCHEDULED_PIPELINES,
  MAX_SCHEDULED_PIPELINES,
} from '../const';
import { humanise } from '../utils/parseSchedule';

import CreateScheduleModal from './CreateScheduleModal';
import {
  CreateScheduleButtonOverlay,
  CreateScheduleButtonWrapper,
  SchedulesDateWrapper,
  SchedulesDialogWrapper,
  SchedulesEmpty,
  SchedulesHeadRow,
  SchedulesList,
  SchedulesRow,
  SchedulesTable,
  SchedulesTitle,
  SchedulesWarning,
  SchedulesWrapper,
} from './styled';

export type Props = {
  createSchedule: (props: any) => void;
  deleteSchedule: (schedule: Schedule) => void;
  disableSchedule: (schedule: Schedule) => void;
  enableSchedule: (schedule: Schedule) => void;
  fetchBranches: (props: any) => any;
  fetchingSchedules: boolean;
  getSchedules: () => void;
  repository: Repository;
  schedules: Schedule[];
  _isDialogOpen?: boolean;
  scheduleStatus: LoadingStatus;
};

/* eslint @typescript-eslint/ban-types: "warn" */
const Schedules: React.FC<Props> = ({
  createSchedule,
  deleteSchedule,
  disableSchedule,
  enableSchedule,
  fetchBranches,
  fetchingSchedules,
  getSchedules,
  repository,
  schedules,
  _isDialogOpen,
  scheduleStatus,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(_isDialogOpen);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<
    false | Schedule
  >(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [cronErrorMessage, setCronErrorMessage] = useState<string>('');

  useEffect(() => {
    if (isDialogOpen) {
      getSchedules();
    }
  }, [getSchedules, isDialogOpen]);

  const toggleShowingDialog = useCallback(() => {
    setIsDialogOpen(!isDialogOpen);
  }, [setIsDialogOpen, isDialogOpen]);

  const onCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, [setIsDialogOpen]);

  const onCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, [setIsDeleteDialogOpen]);

  const onOpenDeleteDialog = useCallback(
    schedule => {
      setIsDeleteDialogOpen(schedule);
    },
    [setIsDeleteDialogOpen]
  );

  const onConfirmDeleteDialog = useCallback(
    schedule => {
      deleteSchedule(schedule);
      onCloseDeleteDialog();
    },
    [deleteSchedule, onCloseDeleteDialog]
  );

  const onOpenCreateDialog = useCallback(
    _schedule => {
      setIsCreateDialogOpen(true);
    },
    [setIsCreateDialogOpen]
  );

  const onCloseCreateDialog = useCallback(
    schedule => {
      if (!(schedule?.nativeEvent instanceof Event)) {
        createSchedule(schedule);
      }
    },
    [createSchedule]
  );
  const onCancelDialog = useCallback(() => {
    setIsCreateDialogOpen(false);
    setCronErrorMessage('');
  }, [setIsCreateDialogOpen]);

  useEffect(() => {
    if (scheduleStatus === LoadingStatus.Success) {
      setIsCreateDialogOpen(false);
      setCronErrorMessage('');
    } else if (scheduleStatus === LoadingStatus.Failed) {
      setCronErrorMessage('Please enter a valid cron expression');
    }
  }, [scheduleStatus, setIsCreateDialogOpen, setCronErrorMessage]);

  const isIncreasedMaxRepoSchedulesEnabled = useStatsigGate(
    StatsigFeatureKeys.isIncreasedMaxRepoSchedulesEnabled
  );

  const SCHEDULED_PIPELINES_LIMIT = isIncreasedMaxRepoSchedulesEnabled
    ? INCREASED_MAX_SCHEDULED_PIPELINES
    : MAX_SCHEDULED_PIPELINES;

  const isCreateDisabled = useMemo(() => {
    return (
      (schedules && schedules.length >= SCHEDULED_PIPELINES_LIMIT) ||
      !repository.canWrite
    );
  }, [schedules, repository, SCHEDULED_PIPELINES_LIMIT]);

  const tooltipContent = useMemo(() => {
    const contentToRender = isCreateDisabled && !fetchingSchedules;

    if (contentToRender && !repository.canWrite) {
      return 'To create scheduled pipelines you need write permission.';
    } else if (contentToRender) {
      return `Maximum ${SCHEDULED_PIPELINES_LIMIT} scheduled pipelines are allowed.`;
    } else {
      return '';
    }
  }, [
    repository,
    isCreateDisabled,
    fetchingSchedules,
    SCHEDULED_PIPELINES_LIMIT,
  ]);

  const onToggleSchedule = useCallback(
    (schedule: Schedule) => {
      if (schedule.enabled) {
        disableSchedule(schedule);
      } else {
        enableSchedule(schedule);
      }
    },
    [disableSchedule, enableSchedule]
  );

  const renderRow = useCallback(
    (schedule: Schedule, key: number) => {
      const scheduleName = (p = { withTypeLabel: false }) => {
        const typeLabel =
          schedule['target.selector.type'] &&
          (p.withTypeLabel || !schedule['target.selector.pattern'])
            ? schedule['target.selector.type']
            : '';
        return `${typeLabel}${
          schedule['target.selector.pattern']
            ? (typeLabel ? ': ' : '') + schedule['target.selector.pattern']
            : ''
        }`;
      };
      return (
        schedule.cron_pattern && (
          <SchedulesRow
            key={schedule.uuid || `schedule_row_${key}`}
            isNew={!!schedule.uuid}
            isEnabled={schedule.enabled}
          >
            <td>
              <Toggle
                isDisabled={!schedule.uuid}
                id="toggle-schedule"
                label="Enable Schedule"
                isChecked={schedule.enabled}
                onChange={() => onToggleSchedule(schedule)}
              />
            </td>
            <td>
              <div title={schedule['target.ref_name']}>
                {schedule['target.ref_name']}
              </div>
            </td>
            <td>
              <Tooltip
                position="bottom"
                content={scheduleName({ withTypeLabel: true })}
              >
                <div>{scheduleName()}</div>
              </Tooltip>
            </td>
            <td>
              <SchedulesDateWrapper>
                <div title={humanise(schedule.cron_pattern)}>
                  {humanise(schedule.cron_pattern)}
                </div>
                {schedule.executions[0]?.['error.message'] ? (
                  <SchedulesWarning>
                    <Tooltip content={schedule.executions[0]['error.message']}>
                      <WarningIcon
                        label={schedule.executions[0]['error.message']}
                        size="small"
                      />
                    </Tooltip>
                  </SchedulesWarning>
                ) : null}
              </SchedulesDateWrapper>
            </td>
            <td>
              {repository.canWrite && schedule.uuid ? (
                <Button
                  appearance="subtle"
                  iconAfter={<IconRemove label="Remove" />}
                  onClick={() => onOpenDeleteDialog(schedule)}
                />
              ) : (
                <Spinner size="small" />
              )}
            </td>
          </SchedulesRow>
        )
      );
    },
    [onOpenDeleteDialog, onToggleSchedule, repository]
  );

  return (
    <>
      <InlineDialog
        content={
          <SchedulesDialogWrapper>
            <SchedulesWrapper>
              <header>
                <SchedulesTitle>Scheduled pipelines</SchedulesTitle>
                <Tooltip position="bottom" content={tooltipContent}>
                  <CreateScheduleButtonWrapper>
                    {(fetchingSchedules || isCreateDisabled) && (
                      <CreateScheduleButtonOverlay />
                    )}
                    <Button
                      isDisabled={fetchingSchedules || isCreateDisabled}
                      onClick={onOpenCreateDialog}
                    >
                      New schedule
                    </Button>
                  </CreateScheduleButtonWrapper>
                </Tooltip>
              </header>
              {fetchingSchedules ? (
                <Spinner size="small" />
              ) : (
                <>
                  <SchedulesTable>
                    {schedules && schedules.length > 0 ? (
                      <thead>
                        <SchedulesHeadRow>
                          <th />
                          <th>Branch</th>
                          <th>Pipeline</th>
                          <th>Schedule</th>
                          <th />
                        </SchedulesHeadRow>
                      </thead>
                    ) : null}
                  </SchedulesTable>
                  <SchedulesList>
                    <SchedulesTable>
                      <tbody>
                        {schedules?.length ? schedules.map(renderRow) : null}
                        {schedules?.length === 0 ? (
                          <tr>
                            <SchedulesEmpty colSpan={5}>
                              <CalendarIcon
                                label="schedule"
                                size="xlarge"
                                primaryColor={token(
                                  'color.text.subtlest',
                                  N100
                                )}
                              />
                              <p>
                                You haven&apos;t configured any schedules yet.{' '}
                                <Button
                                  href={DOCS_SCHEDULED_PIPELINES}
                                  spacing="none"
                                  appearance="link"
                                  target="_blank"
                                >
                                  Learn more
                                </Button>
                              </p>
                            </SchedulesEmpty>
                          </tr>
                        ) : null}
                      </tbody>
                    </SchedulesTable>
                  </SchedulesList>
                </>
              )}
            </SchedulesWrapper>
          </SchedulesDialogWrapper>
        }
        onClose={onCloseDialog}
        isOpen={isDialogOpen}
        placement="bottom-end"
      >
        <Button onClick={toggleShowingDialog}>Schedules</Button>
      </InlineDialog>
      <ModalTransition>
        {isCreateDialogOpen !== false ? (
          <CreateScheduleModal
            fetchBranches={fetchBranches}
            onCloseDialog={onCloseCreateDialog}
            onCancelDialog={onCancelDialog}
            cronErrorMessage={cronErrorMessage}
            scheduleStatus={scheduleStatus}
            showBlanket
          />
        ) : null}
        {isDeleteDialogOpen !== false ? (
          <Modal onClose={onCloseDeleteDialog}>
            <ModalHeader>
              <ModalTitle appearance="danger">Delete schedule</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to delete this schedule?</p>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={onCloseDeleteDialog}>
                Cancel
              </Button>
              <Button
                appearance="danger"
                onClick={() => onConfirmDeleteDialog(isDeleteDialogOpen)}
                autoFocus
              >
                Delete
              </Button>
            </ModalFooter>
          </Modal>
        ) : null}
      </ModalTransition>
    </>
  );
};

export default React.memo(Schedules);
